import * as THREE from 'three';
import { BufferGeometry, Float32BufferAttribute } from 'three';
import { mergeBufferGeometries } from 'three-stdlib';

export default function ChamferedCubeGeometry(size: number, chamfer: number, segments: number) {
  const width = Math.sqrt(Math.pow(chamfer, 2) / 2); // single direction component of chamfer
  const half = size / 2;
  const shortHalf = half - width;
  const edgePos = half - width / 2;
  const face = shortHalf * 2;

  let geometry: BufferGeometry = new BufferGeometry();

  // corners
  const tri = [];
  tri.push(new THREE.Vector3(shortHalf, half, shortHalf));
  tri.push(new THREE.Vector3(shortHalf, shortHalf, half));
  tri.push(new THREE.Vector3(half, shortHalf, shortHalf));
  const corner1 = new THREE.BufferGeometry().setFromPoints(tri);
  corner1.computeVertexNormals();
  corner1.setAttribute('uv', new Float32BufferAttribute([0, 0], 2));

  const tri2 = [];
  tri2.push(new THREE.Vector3(-shortHalf, half, shortHalf));
  tri2.push(new THREE.Vector3(-half, shortHalf, shortHalf));
  tri2.push(new THREE.Vector3(-shortHalf, shortHalf, half));
  const corner2 = new THREE.BufferGeometry().setFromPoints(tri2);
  corner2.computeVertexNormals();
  corner2.setAttribute('uv', new Float32BufferAttribute([0, 0], 2));

  const tri3 = [];
  tri3.push(new THREE.Vector3(half, -shortHalf, shortHalf));
  tri3.push(new THREE.Vector3(shortHalf, -shortHalf, half));
  tri3.push(new THREE.Vector3(shortHalf, -half, shortHalf));
  const corner3 = new THREE.BufferGeometry().setFromPoints(tri3);
  corner3.computeVertexNormals();
  corner3.setAttribute('uv', new Float32BufferAttribute([0, 0], 2));

  const tri4 = [];
  tri4.push(new THREE.Vector3(-shortHalf, -half, shortHalf));
  tri4.push(new THREE.Vector3(-shortHalf, -shortHalf, half));
  tri4.push(new THREE.Vector3(-half, -shortHalf, shortHalf));
  const corner4 = new THREE.BufferGeometry().setFromPoints(tri4);
  corner4.computeVertexNormals();
  corner4.setAttribute('uv', new Float32BufferAttribute([0, 0], 2));

  // edges
  const edge1 = new THREE.PlaneGeometry(chamfer, face, 1, segments);
  edge1.rotateZ(Math.PI * 0.5);
  edge1.rotateX(Math.PI * -0.25);
  edge1.translate(0, edgePos, edgePos);

  const edge2 = new THREE.PlaneGeometry(chamfer, face, 1, segments);
  edge2.rotateY(Math.PI * 0.25);
  edge2.translate(edgePos, 0, edgePos);

  const edge3 = new THREE.PlaneGeometry(chamfer, face, 1, segments);
  edge3.rotateX(Math.PI * -0.5);
  edge3.rotateZ(Math.PI * -0.25);
  edge3.translate(edgePos, edgePos, 0);

  const edge4 = new THREE.PlaneGeometry(chamfer, face, 1, segments);
  edge4.rotateX(Math.PI * 0.5);
  edge4.rotateZ(Math.PI * 0.25);
  edge4.translate(edgePos, -edgePos, 0);

  const edge5 = new THREE.PlaneGeometry(chamfer, face, 1, segments);
  edge5.rotateZ(Math.PI * 0.5);
  edge5.rotateX(Math.PI * 0.25);
  edge5.translate(0, -edgePos, edgePos);

  const edge6 = new THREE.PlaneGeometry(chamfer, face, 1, segments);
  edge6.rotateY(Math.PI * -0.25);
  edge6.translate(-edgePos, 0, edgePos);

  // sides
  // front
  const side1 = new THREE.PlaneGeometry(face, face, segments, segments);
  side1.translate(0, 0, half);
  // right
  const side2 = new THREE.PlaneGeometry(face, face, segments, segments);
  side2.rotateY(Math.PI * 0.5);
  side2.translate(half, 0, 0);

  geometry =
    mergeBufferGeometries(
      [
        corner1,
        corner2,
        corner3,
        corner4,
        edge1.toNonIndexed(),
        edge2.toNonIndexed(),
        edge3.toNonIndexed(),
        edge4.toNonIndexed(),
        edge5.toNonIndexed(),
        edge6.toNonIndexed(),
        side1.toNonIndexed(),
        side2.toNonIndexed()
      ],
      true
    ) || new BufferGeometry();
  const secondHalf = geometry.clone();
  secondHalf.rotateY(Math.PI);

  // top
  const top = new THREE.PlaneGeometry(face, face, segments, segments);
  top.rotateX(-Math.PI * 0.5);
  top.translate(0, half, 0);

  // bottom
  const bottom = new THREE.PlaneGeometry(face, face, segments, segments);
  bottom.rotateX(Math.PI * 0.5);
  bottom.translate(0, -half, 0);

  geometry =
    mergeBufferGeometries(
      [geometry, secondHalf, top.toNonIndexed(), bottom.toNonIndexed()],
      true
    ) || new BufferGeometry();

  return geometry;
}
